import React, { useEffect, useMemo, useState } from "react";

function pad2(value) {
  return String(Number(value || 0)).padStart(2, "0");
}

function rowStatus(row) {
  if (row.status === "applied") return "APPLIED";
  if (row.status === "exact") return "EXACT";
  if (row.status === "no_episode") return "NO EPISODE";
  if (row.status === "no_number") return "NO NUMBER";
  if (row.status === "failed") return "FAILED";
  if (row.already_matched) return "ALREADY MATCHED";
  return "READY";
}

function statusClass(row) {
  if (row.status === "applied") return "text-[var(--phosphor)]";
  if (row.status === "exact") return "text-[var(--phosphor)]";
  if (row.status === "no_episode" || row.status === "no_number") return "text-[#FFAA33]";
  if (row.status === "failed") return "text-[#FF6666]";
  if (row.already_matched) return "opacity-50";
  return "opacity-55";
}

export default function BulkTmdbMatchModal({
  open,
  videos = [],
  apiBase,
  authHeaders,
  onClose,
  onApplied,
  append,
}) {
  const [showQuery, setShowQuery] = useState("");
  const [showSearching, setShowSearching] = useState(false);
  const [showResults, setShowResults] = useState([]);
  const [selectedShow, setSelectedShow] = useState(null);

  const [rows, setRows] = useState([]);
  const [running, setRunning] = useState(false);
  const [applying, setApplying] = useState(false);
  const [error, setError] = useState("");

  // Start a fresh working session only when the modal opens. Do NOT reset
  // whenever PlaylistPanel refreshes `videos` after an apply; that would erase
  // completed groups and recreate the single-series behaviour r1 exposed.
  useEffect(() => {
    if (!open) return;
    setShowQuery("");
    setShowResults([]);
    setSelectedShow(null);
    setRows(
      videos.map((v, index) => ({
        video_id: v.id,
        title: v.title || `Tape ${index + 1}`,
        selected: false,
        status: "idle",
        already_matched: Boolean(v.tmdb_id && v.tmdb_media_type === "tv"),
        episode: null,
        matched_show: null,
      }))
    );
    setError("");
  }, [open]); // intentional session boundary

  const selectedRows = useMemo(
    () => rows.filter((r) => r.selected && r.status !== "applied"),
    [rows]
  );

  const currentExact = useMemo(
    () => rows.filter((r) =>
      r.selected &&
      r.status === "exact" &&
      r.episode &&
      r.matched_show?.tmdb_id === selectedShow?.tmdb_id
    ),
    [rows, selectedShow]
  );

  const counts = useMemo(() => ({
    applied: rows.filter((r) => r.status === "applied").length,
    exact: rows.filter((r) => r.status === "exact").length,
    noEpisode: rows.filter((r) => r.status === "no_episode").length,
    noNumber: rows.filter((r) => r.status === "no_number").length,
    failed: rows.filter((r) => r.status === "failed").length,
  }), [rows]);

  if (!open) return null;

  function setSelectedFor(ids, selected) {
    const wanted = new Set(ids);
    setRows((current) => current.map((row) =>
      wanted.has(row.video_id) && row.status !== "applied"
        ? { ...row, selected }
        : row
    ));
  }

  function clearSelection() {
    setRows((current) => current.map((row) => (
      row.status === "applied" ? row : { ...row, selected: false }
    )));
  }

  function selectAllAvailable() {
    setRows((current) => current.map((row) => (
      row.status === "applied"
        ? row
        : { ...row, selected: true }
    )));
  }

  function selectUnfinished() {
    setRows((current) => current.map((row) => ({
      ...row,
      selected: row.status !== "applied" && !row.already_matched,
    })));
  }

  async function searchShows() {
    const q = showQuery.trim();
    if (!q) return;
    if (!selectedRows.length) {
      setError("Select the tapes that belong to this series first.");
      return;
    }
    setShowSearching(true);
    setError("");
    try {
      const params = new URLSearchParams({ q });
      const res = await fetch(`${apiBase}/tmdb/search?${params.toString()}`, {
        headers: authHeaders(),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.detail || `TMDb search failed (${res.status})`);
      }
      const data = await res.json();
      const tv = (data.results || []).filter((item) => item.media_type === "tv");
      setShowResults(tv);
      append?.(`> BULK TMDB SHOW SEARCH — ${tv.length} TV CANDIDATE(S) FOR ${q}`);
    } catch (e) {
      setError(e.message || "TMDb show search failed.");
      setShowResults([]);
    } finally {
      setShowSearching(false);
    }
  }

  function chooseShow(candidate) {
    if (!selectedRows.length) {
      setError("Select the tapes that belong to this series first.");
      return;
    }
    setSelectedShow(candidate);
    setShowResults([]);
    setError("");
    append?.(
      `> BULK TMDB GROUP SHOW — ${selectedRows.length} TAPE(S) → ` +
      `${candidate.title || "TMDB TV"} (${candidate.tmdb_id})`
    );
  }

  function changeShow() {
    setSelectedShow(null);
    setShowResults([]);
    setShowQuery("");
    setError("");
    // Preserve applied rows and every previous result. Only the active scope
    // selection is cleared so the next series can be chosen safely.
    clearSelection();
  }

  async function matchSelected() {
    if (!selectedShow?.tmdb_id) {
      setError("Search for and select the TMDb TV show for the selected tapes first.");
      return;
    }
    const ids = selectedRows.map((r) => r.video_id);
    if (!ids.length) {
      setError("Select at least one tape for this series.");
      return;
    }

    setRunning(true);
    setError("");
    try {
      const res = await fetch(`${apiBase}/tmdb/playlist-episode-preview`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...authHeaders() },
        body: JSON.stringify({
          tmdb_id: Number(selectedShow.tmdb_id),
          video_ids: ids,
          mode: "all",
        }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.detail || `Episode preview failed (${res.status})`);
      }
      const data = await res.json();
      const previewById = new Map((data.rows || []).map((row) => [row.video_id, row]));

      setRows((current) => current.map((row) => {
        const preview = previewById.get(row.video_id);
        if (!preview) return row;
        return {
          ...row,
          ...preview,
          selected: preview.status === "exact",
          matched_show: {
            tmdb_id: Number(selectedShow.tmdb_id),
            title: data.show_title || selectedShow.title || "TMDb TV",
            year: data.show_year || selectedShow.year || null,
          },
        };
      }));

      append?.(
        `> BULK TMDB GROUP MATCH — ${ids.length} TAPE(S) → ` +
        `${data.show_title || selectedShow.title || "TV"}: ` +
        `${data.exact || 0} EXACT, ${data.no_episode || 0} NO EPISODE, ` +
        `${data.no_number || 0} NO NUMBER`
      );
    } catch (e) {
      setError(e.message || "Bulk TMDb episode match failed.");
    } finally {
      setRunning(false);
    }
  }

  async function applyCurrentGroup() {
    const exact = currentExact;
    if (!exact.length || !selectedShow?.tmdb_id) return;

    setApplying(true);
    setError("");
    try {
      const ids = exact.map((r) => r.video_id);
      const res = await fetch(`${apiBase}/tmdb/playlist-episode-apply`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...authHeaders() },
        body: JSON.stringify({
          tmdb_id: Number(selectedShow.tmdb_id),
          video_ids: ids,
          apply_show_title: true,
        }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.detail || `Apply failed (${res.status})`);
      }
      const data = await res.json();
      const appliedIds = new Set(
        (data.results || [])
          .filter((r) => r.status === "applied")
          .map((r) => r.video_id)
      );

      setRows((current) => current.map((row) => (
        appliedIds.has(row.video_id)
          ? {
              ...row,
              status: "applied",
              selected: false,
              already_matched: true,
            }
          : row
      )));

      append?.(
        `> BULK TMDB GROUP APPLIED — ${data.show_title || selectedShow.title || "TV"}: ` +
        `${data.applied || 0} APPLIED${data.failed ? `, ${data.failed} FAILED` : ""}`
      );

      if (onApplied) await onApplied(data);
    } catch (e) {
      setError(e.message || "Bulk TMDb apply failed.");
    } finally {
      setApplying(false);
    }
  }

  const scopeCount = selectedRows.length;

  return (
    <div className="fixed inset-0 z-[120] bg-black/80 flex items-center justify-center p-6" data-testid="bulk-tmdb-modal">
      <div className="w-[1160px] max-w-[96vw] h-[90vh] bg-black border-2 border-[var(--phosphor)] text-[var(--phosphor)] flex flex-col shadow-[0_0_24px_color-mix(in_srgb,var(--phosphor)_20%,transparent)]">
        <div className="px-4 py-3 border-b border-[color-mix(in_srgb,var(--phosphor)_35%,transparent)] flex items-start justify-between">
          <div>
            <div className="tracking-widest">🎬 BULK TMDB MATCH</div>
            <div className="text-[10px] opacity-60 mt-1">
              CURRENT PLAYLIST · {videos.length} TAPES · select one series group at a time, match it, apply it, then move to the next.
            </div>
          </div>
          <button onClick={onClose} className="text-xl leading-none px-2" aria-label="Close">×</button>
        </div>

        <div className="px-4 py-3 border-b border-[color-mix(in_srgb,var(--phosphor)_25%,transparent)]">
          <div className="flex items-center justify-between gap-4 mb-3">
            <div>
              <div className="text-[9px] tracking-[0.2em] opacity-55">MATCH SCOPE</div>
              <div className="text-[12px] mt-1">
                SELECTED TAPES · {scopeCount}
                {selectedShow ? ` → ${selectedShow.title || "TMDb TV"}${selectedShow.year ? ` (${selectedShow.year})` : ""}` : ""}
              </div>
            </div>
            <div className="flex gap-2">
              <button onClick={selectAllAvailable} disabled={running || applying} className="border border-[var(--phosphor)] px-3 py-1 text-[9px] disabled:opacity-40">SELECT ALL AVAILABLE</button>
              <button onClick={selectUnfinished} disabled={running || applying} className="border border-[var(--phosphor)] px-3 py-1 text-[9px] disabled:opacity-40">SELECT UNMATCHED</button>
              <button onClick={clearSelection} disabled={running || applying} className="border border-[var(--phosphor)] px-3 py-1 text-[9px] disabled:opacity-40">CLEAR</button>
            </div>
          </div>

          {!selectedShow ? (
            <>
              <div className="text-[10px] tracking-[0.22em] opacity-65 mb-2">TMDB TV SHOW FOR SELECTED TAPES · VISUAL SEARCH</div>
              <div className="flex gap-2">
                <input
                  value={showQuery}
                  onChange={(e) => setShowQuery(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter") searchShows(); }}
                  placeholder={scopeCount ? "Search for this group's TV show..." : "Select tapes first..."}
                  disabled={!scopeCount}
                  data-testid="bulk-tmdb-show-search"
                  className="flex-1 bg-black border border-[var(--phosphor)] px-3 py-2 outline-none disabled:opacity-35"
                />
                <button
                  onClick={searchShows}
                  disabled={showSearching || !showQuery.trim() || !scopeCount}
                  className="border border-[var(--phosphor)] px-4 py-2 hover:bg-[var(--phosphor)] hover:text-black disabled:opacity-40"
                >
                  {showSearching ? "SEARCHING…" : "SEARCH"}
                </button>
              </div>

              {showResults.length > 0 && (
                <div className="mt-3">
                  <div className="text-[9px] tracking-[0.18em] opacity-55 mb-2">
                    SEARCH RESULTS · {showResults.length} TV CANDIDATE{showResults.length === 1 ? "" : "S"}
                  </div>
                  <div
                    className="grid grid-cols-4 gap-3 max-h-[360px] overflow-y-auto pr-1"
                    data-testid="bulk-tmdb-visual-results"
                  >
                    {showResults.map((item) => (
                      <button
                        key={item.tmdb_id}
                        type="button"
                        onClick={() => chooseShow(item)}
                        className="text-left border border-[color-mix(in_srgb,var(--phosphor)_35%,transparent)] hover:border-[var(--phosphor)] hover:bg-[color-mix(in_srgb,var(--phosphor)_7%,black)] transition-colors group overflow-hidden"
                        data-testid={`bulk-tmdb-show-result-${item.tmdb_id}`}
                      >
                        <div className="aspect-[2/3] bg-[color-mix(in_srgb,var(--phosphor)_5%,black)] border-b border-[color-mix(in_srgb,var(--phosphor)_20%,transparent)] flex items-center justify-center overflow-hidden">
                          {item.poster_url ? (
                            <img
                              src={item.poster_url}
                              alt=""
                              className="w-full h-full object-cover"
                              loading="lazy"
                            />
                          ) : (
                            <div className="px-3 text-center text-[9px] tracking-[0.16em] opacity-35">
                              NO POSTER AVAILABLE
                            </div>
                          )}
                        </div>

                        <div className="p-3">
                          <div className="text-[12px] leading-tight min-h-[30px]">
                            {item.title || "Untitled TV show"}
                          </div>
                          <div className="text-[9px] opacity-55 mt-1">
                            {item.year || "YEAR ?"} · TMDb #{item.tmdb_id}
                          </div>
                          <div className="text-[9px] opacity-60 mt-2 leading-relaxed line-clamp-3 min-h-[42px]">
                            {item.overview || "No programme description available from TMDb."}
                          </div>
                          <div className="mt-3 border border-[var(--phosphor)] px-2 py-1 text-center text-[9px] tracking-[0.12em] group-hover:bg-[var(--phosphor)] group-hover:text-black">
                            USE FOR SELECTED ({scopeCount})
                          </div>
                        </div>
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </>
          ) : (
            <div className="flex items-center justify-between gap-4 border border-[var(--phosphor)] px-3 py-2">
              <div>
                <div className="text-[9px] tracking-[0.2em] opacity-55">CURRENT GROUP SHOW</div>
                <div className="text-[13px] mt-1">
                  {selectedShow.title || "TMDb TV"} {selectedShow.year ? `(${selectedShow.year})` : ""} · TMDb #{selectedShow.tmdb_id}
                  <span className="opacity-55"> · {scopeCount} SELECTED</span>
                </div>
              </div>
              <button
                onClick={changeShow}
                disabled={running || applying}
                className="border border-[var(--phosphor)] px-3 py-1 text-[10px] disabled:opacity-40"
              >
                NEXT / CHANGE SHOW
              </button>
            </div>
          )}

          {error && <div className="mt-3 text-[#FF6666] text-[10px]">{error}</div>}
        </div>

        <div className="px-4 py-3 border-b border-[color-mix(in_srgb,var(--phosphor)_25%,transparent)] flex items-center justify-between gap-4">
          <div className="flex gap-2">
            <button
              onClick={matchSelected}
              disabled={!selectedShow || !scopeCount || running || applying}
              className="border border-[var(--phosphor)] px-3 py-1 hover:bg-[var(--phosphor)] hover:text-black disabled:opacity-40"
            >
              {running ? "MATCHING…" : `MATCH SELECTED (${scopeCount})`}
            </button>
          </div>
          <div className="text-[9px] tracking-[0.14em] opacity-70">
            APPLIED {counts.applied} · EXACT {counts.exact} · NO EPISODE {counts.noEpisode} · NO NUMBER {counts.noNumber} · FAILED {counts.failed}
          </div>
        </div>

        <div className="flex-1 overflow-y-auto px-4">
          {rows.map((row, index) => (
            <div key={row.video_id || index} className="grid grid-cols-[28px_1fr_1fr_125px] gap-3 items-center min-h-[38px] border-b border-[color-mix(in_srgb,var(--phosphor)_16%,transparent)] text-[10px]">
              <input
                type="checkbox"
                checked={Boolean(row.selected)}
                disabled={row.status === "applied" || running || applying}
                onChange={(e) => setSelectedFor([row.video_id], e.target.checked)}
              />
              <div className="truncate">
                {String(index + 1).padStart(2, "0")}. {row.title}
              </div>
              <div className="truncate opacity-70">
                {row.status === "applied" && row.matched_show
                  ? `${row.matched_show.title} · TMDb S${pad2(row.episode?.season_number)}E${pad2(row.episode?.episode_number)}`
                  : row.episode
                    ? `${row.matched_show?.title || "TMDb"} · S${pad2(row.episode.season_number)}E${pad2(row.episode.episode_number)} · ${row.episode.name || "Episode"}`
                    : row.matched_show
                      ? `${row.matched_show.title} · no exact episode`
                      : "—"}
              </div>
              <div className={`text-right ${statusClass(row)}`}>{rowStatus(row)}</div>
            </div>
          ))}
        </div>

        <div className="px-4 py-3 border-t border-[color-mix(in_srgb,var(--phosphor)_35%,transparent)] flex items-center justify-between gap-3">
          <div className="text-[9px] opacity-55">
            TIP: APPLY THIS GROUP, THEN NEXT / CHANGE SHOW AND SELECT THE NEXT SERIES.
          </div>
          <div className="flex gap-2">
            <button onClick={onClose} disabled={applying} className="border border-[var(--phosphor)] px-4 py-1 text-[10px]">CANCEL</button>
            <button
              onClick={applyCurrentGroup}
              disabled={running || applying || currentExact.length === 0}
              className="bg-[var(--phosphor)] text-black px-4 py-1 text-[10px] tracking-widest font-bold disabled:opacity-40"
            >
              {applying ? "APPLYING…" : `APPLY CURRENT GROUP (${currentExact.length})`}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
